import Pubnub from 'pubnub';
import { UrlUnfurled } from '../types/urlUnfurled';

/**
 * Dependencies interface for parseUrlUnfurled function
 * Supports dependency injection for testing and customization
 */
interface ParseUrlUnfurledDependencies {
  /** Function to log errors during parsing */
  logError: (message: string, error?: unknown) => void;
}

/**
 * Default dependencies for parseUrlUnfurled function
 * Uses console.error for error logging
 */
const defaultParseUrlUnfurledDeps: ParseUrlUnfurledDependencies = {
  logError: (message, error) => console.error(message, error),
};

/**
 * Parse URL unfurled data from PubNub message
 *
 * Handles two different PubNub message formats:
 * 1. Real-time format: message.payload.data or message.data.data
 * 2. History format: message.data.attributes (nested structure)
 *
 * Extracts the following fields:
 * - id: Unique identifier for the URL unfurled event
 * - originalMessageId: ID of the message that contained the URL
 * - sessionId: Conversation/session ID
 * - unfurledUrl: Object containing url, title, description, image, favicon, unfurledAt
 *
 * @param message - PubNub message containing URL unfurled event (either real-time or history format)
 * @param deps - Optional dependencies object for dependency injection (defaults to real implementations)
 * @returns Parsed UrlUnfurled object with conversationId set from sessionId, or null if parsing fails
 *
 * @example
 * ```typescript
 * const urlUnfurled = parseUrlUnfurled(pubnubMessage);
 * if (urlUnfurled) {
 *   console.log('Preview URL:', urlUnfurled.unfurledUrl.url);
 * }
 * ```
 */
export function parseUrlUnfurled(
  message: Pubnub.MessageEvent['message'],
  deps: Partial<ParseUrlUnfurledDependencies> = {},
): UrlUnfurled | null {
  const { logError = defaultParseUrlUnfurledDeps.logError } = deps;

  try {
    const messageAny = message as any;
    
    // Handle different message formats:
    // 1. Real-time PubNub: message.payload.data or message.data.data
    // 2. History PubNub: message.data with attributes nested
    let urlUnfurledData: any;
    let id: string;
    let originalMessageId: string;
    let sessionId: string;
    let unfurledUrl: any;

    // Check for history format first (message.data.attributes exists)
    if (messageAny.data?.attributes) {
      // History format: data is in message.data.attributes
      const { attributes, id: dataId } = messageAny.data;
      id = dataId;
      originalMessageId = attributes.originalMessageId;
      sessionId = attributes.sessionId;
      unfurledUrl = attributes.unfurledUrl;
    } else {
      // Real-time format: check payload.data or data.data
      const payload = messageAny.payload || messageAny.data;
      if (!payload || !payload.data) {
        logError('Invalid URL unfurled message format: missing payload.data or data.attributes', message);
        return null;
      }
      urlUnfurledData = payload.data;
      id = urlUnfurledData.id;
      originalMessageId = urlUnfurledData.originalMessageId;
      sessionId = urlUnfurledData.sessionId;
      unfurledUrl = urlUnfurledData.unfurledUrl;
    }

    if (!id || !originalMessageId || !sessionId || !unfurledUrl) {
      logError('Invalid URL unfurled message: missing required fields', {
        id,
        originalMessageId,
        sessionId,
        unfurledUrl,
      });
      return null;
    }

    if (!unfurledUrl.url) {
      logError('Invalid URL unfurled message: missing unfurledUrl.url', { unfurledUrl });
      return null;
    }

    return {
      id,
      originalMessageId,
      sessionId,
      unfurledUrl: {
        url: unfurledUrl.url,
        title: unfurledUrl.title,
        description: unfurledUrl.description,
        image: unfurledUrl.image,
        favicon: unfurledUrl.favicon,
        unfurledAt: unfurledUrl.unfurledAt,
      },
      conversationId: sessionId,
    };
  } catch (error) {
    logError('Failed to parse URL unfurled message', error);
    return null;
  }
}
